const $$splitComponentImporter = () => import('/home/runner/work/vision-lab-frontend/vision-lab-frontend/src/routes/_authenticated/_app/delivery/$deliveryId.tsx?tsr-split=component');
const $$splitErrorComponentImporter = () => import('/home/runner/work/vision-lab-frontend/vision-lab-frontend/src/routes/_authenticated/_app/delivery/$deliveryId.tsx?tsr-split=errorComponent');
import { lazyRouteComponent } from '@tanstack/react-router';
import { requireCapabilities } from '@/features/authentication/lib/routeGuards';
import { deliveryQueryOptions } from '@/features/delivery/services/deliveryQueries';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

/**
 * `?batch=` is this page's **only** search param: an OPTIONAL filter holding one
 * `batchNo` — the one key shared by a delivery batch and its mirrored package batch.
 * Absent (or unparseable) means no filter, which is the page's default: every entry in
 * the delivery, in `(batchNo, ordinal)` order. It is in the URL rather than in component
 * state so a narrowed view stays linkable and survives a reload, which is the whole
 * reason the batch is worth a search param now that it is not a destination.
 *
 * `.catch(undefined)` makes a malformed value the unnarrowed listing instead of a route
 * error — the fallback is the page's own default, so there is nothing to recover from.
 * A well-formed number that no batch has is NOT corrected here or anywhere: the table
 * reports it as an empty batch, which is also the truth for a batch whose every slot was
 * voided. There are no tabs, so nothing else needs persisting.
 */
const deliveryDetailSearchSchema = z.object({
  batch: z.coerce.number().int().nonnegative().optional().catch(undefined)
});

/**
 * Delivery detail page (`/delivery/:deliveryId`) — the aggregate view with its
 * entries and per-entry outputs, plus the rebuild/void/cancel/complete actions. The
 * section's `manage` guard is inherited from the layout route.
 *
 * The loader prefetches the delivery so this route owns the loading skeleton
 * (`pendingComponent`) and initial-fetch error UI (`errorComponent`);
 * `DeliveryDetail` reads the warm cache through `useDeliverySuspense`, which is what
 * lets it treat the delivery as present — a `deliveryId` that names nothing fails in
 * the loader and lands on `errorComponent`, never on a not-found body. Its child reads
 * (summary, entries, packages) keep their own in-component loading states.
 */
export const Route = createFileRoute('/_authenticated/_app/delivery/$deliveryId')({
  beforeLoad: requireCapabilities(has => has('deliveries.manage')),
  validateSearch: deliveryDetailSearchSchema,
  loader: ({
    context,
    params
  }) => context.queryClient.ensureQueryData(deliveryQueryOptions(params.deliveryId)),
  // Mirrors the shell: a one-line header above the body region (identity + the
  // delivery's entries), not the old header-card + tab-panel pair.
  pendingComponent: () => <div className="flex min-h-0 flex-1 flex-col gap-3 px-6 py-4">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="min-h-48 flex-1" />
    </div>,
  errorComponent: lazyRouteComponent($$splitErrorComponentImporter, 'errorComponent'),
  component: lazyRouteComponent($$splitComponentImporter, 'component')
});